import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Inbox, FileText, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import AdminRatesCard from "@/components/AdminRatesCard";
import AdminCrewSplit from "@/components/AdminCrewSplit";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sora_admin_token";

const STATUSES = {
  new: { label: "New", cls: "text-sky-400 border-sky-500/30 bg-sky-500/10" },
  contacted: { label: "Contacted", cls: "text-sky-400 border-sky-500/30 bg-sky-500/10" },
  booked: { label: "Booked", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
};

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [availability, setAvailability] = useState("");
  const [savingAvailability, setSavingAvailability] = useState(false);

  const loadAvailability = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/settings/availability`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setAvailability(String(data.crews_available));
    } catch {
      // leave blank; admin can still type a value and save
    }
  }, []);

  const saveAvailability = async () => {
    const n = parseInt(availability, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a valid crew number.");
      return;
    }
    setSavingAvailability(true);
    try {
      await axios.put(
        `${API}/settings/availability`,
        { crews_available: n },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Weekend availability updated — live on the site now.");
    } catch {
      toast.error("Could not save availability.");
    } finally {
      setSavingAvailability(false);
    }
  };

  const load = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setLeads(
        [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    } catch (e) {
      if ([401, 403].includes(e.response?.status)) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load leads.");
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      load(token);
      loadAvailability(token);
    }
  }, [token, load, loadAvailability]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lead deleted.");
      load(token);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/leads/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((ls) =>
        ls.map((l) => (l.id === id ? { ...l, status } : l))
      );
      toast.success(`Marked as ${STATUSES[status].label}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const downloadQuote = async (id) => {
    try {
      const res = await axios.get(`${API}/leads/${id}/quote`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sora-Quote-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded.");
    } catch {
      toast.error("Could not generate quote PDF.");
    }
  };

  const sendQuote = async (id) => {
    if (!window.confirm("Email the quote link to this client now?")) return;
    try {
      const { data } = await axios.post(
        `${API}/leads/${id}/send-quote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((ls) =>
        ls.map((x) => (x.id === id ? { ...x, quote_sent_at: data.quote_sent_at } : x))
      );
      toast.success("Quote emailed to the client.");
    } catch {
      toast.error("Could not send quote email.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#090A0F] text-white flex items-center justify-center px-4">
        <Toaster richColors position="top-right" />
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm bg-[#131622] border border-white/10 rounded-md p-8"
        >
          <img
            src="/sora-logo.png"
            alt="Sora"
            className="w-12 h-12 rounded-sm mx-auto object-cover"
          />
          <h1 className="font-display text-2xl font-bold text-center mt-4 uppercase tracking-tight">
            Sora <span className="text-sky-500">Leads</span>
          </h1>
          <p className="text-sm text-slate-400 text-center mt-1 mb-6">
            Admin access only
          </p>
          <div className="space-y-3">
            <Input
              data-testid="admin-login-email-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0F111A] border-white/10 text-white placeholder:text-slate-500 rounded-sm focus-visible:ring-sky-500"
            />
            <Input
              data-testid="admin-login-password-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0F111A] border-white/10 text-white placeholder:text-slate-500 rounded-sm focus-visible:ring-sky-500"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loggingIn}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-[#090A0F] font-bold rounded-sm px-6 py-3 transition-colors duration-200"
          >
            {loggingIn && <Loader2 className="w-4 h-4 animate-spin" />}
            {loggingIn ? "Signing in..." : "Sign In"}
          </button>
          <a
            href="/"
            data-testid="admin-back-to-site"
            className="block text-center mt-4 text-xs text-slate-500 hover:text-sky-400 transition-colors"
          >
            Back to site
          </a>
        </form>
      </div>
    );
  }

  const all = leads || [];
  const filtered = all.filter((l) => filter === "all" || l.lead_type === filter);
  const quotes = all.filter((l) => l.lead_type === "client_quote").length;
  const applicants = all.filter((l) => l.lead_type === "staff_applicant").length;
  const booked = all.filter((l) => (l.status || "new") === "booked").length;
  const week = all.filter(
    (l) => Date.now() - new Date(l.created_at).getTime() < 7 * 864e5
  ).length;

  const stats = [
    { label: "Total Leads", value: all.length, testid: "admin-stat-total" },
    { label: "Quote Requests", value: quotes, testid: "admin-stat-quotes" },
    { label: "Crew Applications", value: applicants, testid: "admin-stat-applicants" },
    { label: "Booked Jobs", value: booked, testid: "admin-stat-booked" },
    { label: "Last 7 Days", value: week, testid: "admin-stat-week" },
  ];

  const tabs = [
    { key: "all", label: "All", testid: "admin-tab-all" },
    { key: "client_quote", label: "Quote Requests", testid: "admin-tab-quotes" },
    { key: "staff_applicant", label: "Crew Applications", testid: "admin-tab-applicants" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#090A0F] text-white">
      <Toaster richColors position="top-right" />
      <header className="border-b border-white/10 bg-[#0F111A] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/sora-logo.png"
              alt="Sora"
              className="w-8 h-8 rounded-sm object-cover"
            />
            <span className="font-display font-bold text-lg uppercase tracking-tight">
              Leads <span className="text-sky-500">Dashboard</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              data-testid="admin-view-site-link"
              className="text-sm text-slate-400 hover:text-sky-400 px-3 transition-colors"
            >
              View site
            </a>
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="p-2 text-slate-300 hover:text-sky-400 transition-colors"
              aria-label="Refresh leads"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm border border-white/15 rounded-sm px-3 py-1.5 hover:border-sky-500/50 hover:text-sky-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className="bg-[#131622] border border-white/10 rounded-md p-5"
            >
              <p className="font-mono-tactical text-3xl font-bold text-sky-500">
                {s.value}
              </p>
              <p className="mt-1 text-sm text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-[#131622] border border-sky-500/25 rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-display font-bold text-white uppercase tracking-tight">
              Weekend Crew Availability
            </p>
            <p className="text-sm text-slate-400 mt-1">
              This number shows live in the site's activity ticker. Update it as
              your roster changes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              data-testid="admin-availability-input"
              type="number"
              min="0"
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              className="w-28 bg-[#0F111A] border-white/10 text-white rounded-sm focus-visible:ring-sky-500"
            />
            <button
              data-testid="admin-availability-save-button"
              onClick={saveAvailability}
              disabled={savingAvailability}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-[#090A0F] font-bold rounded-sm px-5 py-2 text-sm transition-colors duration-200"
            >
              {savingAvailability && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </div>

        <AdminRatesCard token={token} />

        <div className="mt-8 flex gap-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setFilter(t.key)}
              className={`text-sm rounded-full px-4 py-2 border transition-colors duration-200 ${
                filter === t.key
                  ? "bg-sky-500 text-[#090A0F] border-sky-500 font-semibold"
                  : "border-white/15 text-slate-300 hover:border-sky-500/50 hover:text-sky-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {leads === null ? (
          <div className="mt-10 flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 text-center py-20 border border-dashed border-white/10 rounded-md">
            <Inbox className="w-8 h-8 text-slate-600 mx-auto" />
            <p className="mt-3 text-slate-400">No leads here yet.</p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto border border-white/10 rounded-md">
            <table data-testid="admin-leads-table" className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F111A] text-left font-mono-tactical text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Event / Role</th>
                  <th className="px-4 py-3">Details</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const status = l.status || "new";
                  const stale =
                    status === "new" &&
                    Date.now() - new Date(l.created_at).getTime() > 864e5;
                  return (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${l.id}`}
                      className={`border-t border-white/5 hover:bg-white/[0.02] align-top ${stale ? "bg-red-500/[0.04]" : ""}`}
                    >
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                        {fmtDate(l.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`font-mono-tactical text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 border ${
                            l.lead_type === "client_quote"
                              ? "text-sky-400 border-sky-500/30 bg-sky-500/10"
                              : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                          }`}
                        >
                          {l.lead_type === "client_quote" ? "Quote" : "Applicant"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {stale && (
                            <span
                              data-testid={`admin-stale-flag-${l.id}`}
                              title="Unanswered for over 24 hours"
                              className="w-2 h-2 rounded-full bg-red-500 stale-pulse shrink-0"
                            />
                          )}
                          <Select
                            value={status}
                            onValueChange={(v) => updateStatus(l.id, v)}
                          >
                            <SelectTrigger
                              data-testid={`admin-status-select-${l.id}`}
                              className={`h-7 w-[118px] text-[11px] font-mono-tactical uppercase tracking-widest rounded-full border ${STATUSES[status].cls}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#131622] border-white/10 text-white">
                              {Object.entries(STATUSES).map(([key, s]) => (
                                <SelectItem key={key} value={key}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {stale && (
                          <p className="mt-1.5 font-mono-tactical text-[9px] uppercase tracking-widest text-red-400">
                            Follow up — 24h+ old
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-white">{l.contact_name}</p>
                        {l.company_name && (
                          <p className="text-slate-400">{l.company_name}</p>
                        )}
                        <p className="text-slate-500">{l.email}</p>
                        {l.phone && <p className="text-slate-500">{l.phone}</p>}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {l.event_name && <p>{l.event_name}</p>}
                        {l.event_type && <p className="capitalize">{l.event_type}</p>}
                        {l.location && <p className="text-slate-500">{l.location}</p>}
                        {l.event_date && <p className="text-slate-500">{l.event_date}</p>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 max-w-[240px]">
                        {(l.roles_needed || []).length > 0 && (
                          <p>Roles: {l.roles_needed.join(", ")}</p>
                        )}
                        {l.staff_count && <p>Staff: {l.staff_count}</p>}
                        {l.attendance && <p>Crowd: {l.attendance}</p>}
                        {l.notes && <p className="text-slate-500 mt-1">{l.notes}</p>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {l.lead_type === "client_quote" && (
                          <AdminCrewSplit
                            lead={l}
                            token={token}
                            onSaved={(split) =>
                              setLeads((ls) =>
                                ls.map((x) =>
                                  x.id === l.id ? { ...x, crew_split: split } : x
                                )
                              )
                            }
                          />
                        )}
                        {status === "booked" && l.lead_type === "client_quote" && (
                          <>
                            <button
                              data-testid={`admin-send-quote-${l.id}`}
                              onClick={() => sendQuote(l.id)}
                              className="p-2 text-slate-500 hover:text-sky-400 transition-colors"
                              aria-label="Email quote to client"
                              title="Email quote to client"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`admin-quote-pdf-${l.id}`}
                              onClick={() => downloadQuote(l.id)}
                              className="p-2 text-slate-500 hover:text-sky-400 transition-colors"
                              aria-label="Download quote PDF"
                              title="Download branded quote PDF"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          data-testid={`admin-delete-${l.id}`}
                          onClick={() => remove(l.id)}
                          className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                          aria-label="Delete lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {l.accepted_at && (
                          <p className="font-mono-tactical text-[9px] uppercase tracking-widest text-emerald-400 mt-1">
                            Accepted online
                          </p>
                        )}
                        {l.quote_sent_at && !l.accepted_at && (
                          <p className="font-mono-tactical text-[9px] uppercase tracking-widest text-emerald-400 mt-1">
                            Quote sent
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
